import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Accordion } from '../../components/accordion'
import { ImageUploadField } from '../../components/images/image-upload-field'
import { HeaderSubheader } from '../../components/typography/HeaderSubheader'
import { EditInput } from '../../components/inputs/editInput'
import CustSwitch from '../../components/switch'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Button } from '../../components/button'
import { BackButton } from '../../components/button/backButton'
import Icon from '../../components/icon'
import ComboBox from '../../components/inputs/combobox'
import { AddLocationModal } from '../../components/modal/app_modals/addLocationModal'
import { AddPOSModal } from '../../components/modal/app_modals/addPOSModal'
import { ConfirmationModal } from '../../components/modal/app_modals/confirmationModal'
import { SelectCreatorModal } from '../../components/modal/app_modals/selectCreatorModal'
import Spinner from '../../components/spinner/Spinner'
import { TableComponent } from '../../components/table'
import { PageHeader } from '../../components/typography/PageHeader'
import { getPageFromSkip, getSkipFromPage } from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import Layout from '../../layout'
import { useGetAllOriginsQuery } from '../../redux-toolkits/origin/origin.slice'
import {
  useActivateOrDeactivateTerminalMutation,
  useGetTerminalsQuery,
} from '../../redux-toolkits/terminals/terminals.slice'
import {
  FetchTerminalQuery,
  SingleTerminal,
} from '../../redux-toolkits/terminals/terminals.types'
import {
  useAddFulfillmentCenterCreatorsMutation,
  useEditFulfilmentCenterMutation,
  useGetFulfilmentCenterQuery,
  useGetFulfilmentCentersQuery,
  useRemoveFulfillmentCenterCreatorsMutation,
} from '../../redux-toolkits/vendors/vendors.slice'
import { FulfilmentCentreRole } from '../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import { QueryProps } from '../../redux-toolkits/shared/shared.type'
import {
  editFulfilmentCenterSchema,
  editFulfilmentCenterDetailsSchema,
} from '../../validation/validate'

export const SingleFulfilmentCenterPage = () => {
  const [editIndex, setEditIndex] = useState<number>()
  const [deviceAction, setDeviceAction] = useState('')
  const [selectedCreator, setSelectedCreator] = useState<{
    name: string
    _id: string
  }>()
  const [selectedPOSDevice, setSelectedPOSDevice] =
    useState<SingleTerminal | null>()
  const [query, setQuery] = useState<QueryProps>({
    skip: 0,
    limit: 500,
  })

  const [posQuery, setPOSQuery] = useState<FetchTerminalQuery>({
    page: 1,
    limit: 20,
  })

  const [isPOSModal, setIsPOSModal] = useState(false)

  const { id } = useParams()
  const { data: allCentres } = useGetFulfilmentCentersQuery({})

  const { data: origin, isFetching: fetchingOrigin } = useGetAllOriginsQuery({})

  const [editFulfilmentCenter, { isLoading: editingFulfilmentCenter }] =
    useEditFulfilmentCenterMutation()

  const [addCreators, { isLoading: addingCreators }] =
    useAddFulfillmentCenterCreatorsMutation()

  const [removeCreators, { isLoading: removingCreators }] =
    useRemoveFulfillmentCenterCreatorsMutation()

  const [action, { isLoading: isAction }] =
    useActivateOrDeactivateTerminalMutation()

  const {
    data: fulfilmenntCenterDetails,
    isFetching: fetchingFulfilmenntCenterDetails,
    isLoading: loadingFulfilmenntCenterDetails,
    refetch: refetchFulfilmenntCenterDetails,
  } = useGetFulfilmentCenterQuery(
    {
      id: id ?? '',
      ...query,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const { data: pos, isFetching: fetchingPOS } = useGetTerminalsQuery(
    {
      fulfilmentCentreId: id ?? '',
      ...posQuery,
    },
    {
      skip:
        !fulfilmenntCenterDetails ||
        fulfilmenntCenterDetails.data.role === 'PARENT',
      refetchOnMountOrArgChange: true,
    },
  )

  const { modal, modalOpen, handleModalClose, handleModalOpen, setModalOpen } =
    useModalControl()

  const { values, handleChange, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues: {
        name: fulfilmenntCenterDetails?.data?.name ?? '',
        role: fulfilmenntCenterDetails?.data.role ?? 'CHILD',
        parentFulfilmentCentre:
          fulfilmenntCenterDetails?.data.parentFulfilmentCentre ?? null,
        isActive: fulfilmenntCenterDetails?.data.isActive !== false,
        coordinates: fulfilmenntCenterDetails?.data?.location?.coordinates,
        address: fulfilmenntCenterDetails?.data?.address ?? '',
        domain: fulfilmenntCenterDetails?.data?.domain,

        config: {
          lifestyle: fulfilmenntCenterDetails?.data?.config?.lifestyle ?? true,
          event: fulfilmenntCenterDetails?.data?.config?.event ?? true,
          food: [true, 'true'].includes(
            fulfilmenntCenterDetails?.data?.config?.food ?? true,
          ),
        },
        origin: fulfilmenntCenterDetails?.data?.origin
          ? {
              _id: fulfilmenntCenterDetails.data.origin._id,
              name: fulfilmenntCenterDetails.data.origin.name,
            }
          : undefined,
      },
      enableReinitialize: true,
      validationSchema: editFulfilmentCenterDetailsSchema.shape({
        domain: Yup.string().when('role', {
          is: 'PARENT',
          then: (schema) =>
            schema
              .trim()
              .required('Domain is required for a parent storefront'),
          otherwise: (schema) => schema.optional().nullable(),
        }),
        address: Yup.string().when('role', {
          is: 'CHILD',
          then: (schema) =>
            schema.required('Select the fulfilment centre address'),
        }),
        coordinates: Yup.array().when('role', {
          is: 'CHILD',
          then: (schema) =>
            schema.length(2).required('Select the fulfilment centre address'),
        }),
      }),
      onSubmit: async (values) => {
        const resp = await editFulfilmentCenter({
          id: id ?? '',
          ...values,
          domain: values.domain || undefined,
          parentFulfilmentCentre:
            values.role === 'PARENT' ? null : values.parentFulfilmentCentre,
          address: values.role === 'PARENT' ? undefined : values.address,
          coordinates:
            values.role === 'PARENT' ? undefined : values.coordinates,

          config: {
            lifestyle: values.config.lifestyle,
            event: values.config.event,
            food: values.config.food,
          },
        })

        if (resp.data?.success) {
          setEditIndex(undefined)
        }
      },
    })

  return (
    <Layout>
      <div className="flex flex-col gap-10">
        <BackButton />

        <PageHeader title="Fulfilment Center Information" className="!mb-0" />

        {loadingFulfilmenntCenterDetails ? (
          <Spinner fullScreen={false} />
        ) : (
          <div className="flex flex-col gap-6 w-full max-w-[800px]">
            <div className="flex flex-col gap-6 pb-10 border-b border-divider-gray">
              <HeaderSubheader
                title="Fulfilment Center Image"
                text="Upload fulfilment center image"
              />
              <ImageUploadField
                imageUrl={fulfilmenntCenterDetails?.data.image}
                loading={editingFulfilmentCenter}
                onChange={(image) => {
                  void editFulfilmentCenter({
                    id: id ?? '',
                    name: fulfilmenntCenterDetails?.data.name ?? values.name,
                    image,
                  })
                }}
              />
            </div>

            <EditInput
              title="Fulfilment center name"
              value={values.name}
              onChange={handleChange}
              type="text"
              placeholder="Fulfilment center name"
              onEditSave={async () => {
                handleSubmit()
              }}
              editing={editIndex === 1}
              onEditClick={() => {
                setEditIndex(1)
              }}
              onCancel={() => {
                setFieldValue('name', fulfilmenntCenterDetails?.data?.name)
                setEditIndex(undefined)
              }}
              loading={editingFulfilmentCenter}
              inputName="name"
              className="border-b pb-6"
              errors={touched.name && errors.name ? errors.name : ''}
            />

            {values.role !== 'PARENT' && (
              <EditInput
                title="Fulfilment center address"
                value={`${values.address} (Origin: ${
                  values.origin?.name ?? 'N/A'
                })`}
                onChange={handleChange}
                type="text"
                placeholder="Fulfilment center address"
                onEditSave={() => {
                  setEditIndex(undefined)
                }}
                editing={false}
                onEditClick={() => {
                  handleModalOpen('editFulfilmentCenterAddressModal')
                }}
                onCancel={() => {
                  setEditIndex(undefined)
                }}
                errors={
                  touched.address && errors.address
                    ? errors.address
                    : touched.coordinates && errors.coordinates
                      ? String(errors.coordinates)
                      : ''
                }
                loading={false}
                inputName="address"
                className="border-b pb-6"
              />
            )}

            <EditInput
              title="Domain"
              value={values.domain}
              onChange={handleChange}
              type="text"
              placeholder="Fulfilment center domain"
              onEditSave={async () => {
                handleSubmit()
              }}
              editing={editIndex === 2}
              onEditClick={() => {
                setEditIndex(2)
              }}
              onCancel={() => {
                setFieldValue('domain', fulfilmenntCenterDetails?.data?.domain)
                setEditIndex(undefined)
              }}
              loading={editingFulfilmentCenter}
              inputName="domain"
              className="border-b pb-6"
              errors={touched.domain && errors.domain ? errors.domain : ''}
            />

            <EditInput
              title="Centre type"
              value={
                values.role === 'PARENT'
                  ? 'Parent storefront'
                  : 'Physical fulfilment centre'
              }
              editing={editIndex === 3}
              onEditClick={() => setEditIndex(3)}
              onCancel={() => {
                setFieldValue(
                  'role',
                  fulfilmenntCenterDetails?.data.role ?? 'CHILD',
                )
                setFieldValue(
                  'parentFulfilmentCentre',
                  fulfilmenntCenterDetails?.data.parentFulfilmentCentre ?? null,
                )
                setEditIndex(undefined)
              }}
              onEditSave={handleSubmit}
              loading={editingFulfilmentCenter}
              className="border-b pb-6"
            >
              {({ isEditing }) =>
                isEditing ? (
                  <div className="flex flex-col gap-4">
                    <ComboBox
                      aria-label="Centre type"
                      value={
                        values.role === 'PARENT'
                          ? 'Parent storefront'
                          : 'Physical fulfilment centre'
                      }
                      menuItems={[
                        { name: 'Physical fulfilment centre', value: 'CHILD' },
                        { name: 'Parent storefront', value: 'PARENT' },
                      ]}
                      onSelectOption={(role) => setFieldValue('role', role)}
                      onChange={() => {}}
                      readOnly
                      hasIcon={false}
                      isDropdown
                      immediate
                    />
                    <p className="text-sm text-sec-black">
                      All active physical fulfilment centres are automatically
                      available through the parent storefront. No parent
                      assignment is needed.
                    </p>
                  </div>
                ) : (
                  <p className="text-sec-black">
                    {values.role === 'PARENT'
                      ? 'Parent storefront'
                      : `Physical fulfilment centre (Parent: ${
                          allCentres?.data.docs.find(
                            (centre) =>
                              centre.role === FulfilmentCentreRole.PARENT &&
                              centre.isActive !== false,
                          )?.name ?? 'Automatic'
                        })`}
                  </p>
                )
              }
            </EditInput>

            <div className="border-b pb-6">
              <Accordion
                title="Config"
                className="[&>button>p]:!font-medium [&>button>p]:!text-base"
              >
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between gap-3 border rounded-lg px-4 py-3">
                    <p className="font-medium">Active</p>
                    <CustSwitch
                      checked={values.isActive}
                      onChange={(checked) => setFieldValue('isActive', checked)}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {(
                      [
                        ['lifestyle', 'Lifestyle'],
                        ['event', 'Event'],
                        ['food', 'Food'],
                      ] as const
                    ).map(([key, label]) => {
                      const checkedValue = values.config[key]
                      return (
                        <div
                          key={key}
                          className="flex items-center justify-between gap-3 border rounded-lg px-4 py-3"
                        >
                          <p className="font-medium">{label}</p>
                          <CustSwitch
                            checked={checkedValue}
                            onChange={(checked) => {
                              setFieldValue(`config.${key}`, checked)
                            }}
                          />
                        </div>
                      )
                    })}
                  </div>

                  <Button
                    type="button"
                    primary
                    label="Save Config"
                    className="max-w-[180px]"
                    loading={editingFulfilmentCenter}
                    disabled={editingFulfilmentCenter}
                    onClick={() => {
                      handleSubmit()
                    }}
                  />
                </div>
              </Accordion>
            </div>

            {fulfilmenntCenterDetails?.data.role !== 'PARENT' && (
              <>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium text-base">POS Terminals</p>

                    <button
                      type="button"
                      className="w-fit text-left text-base flex items-center gap-2"
                      onClick={() => {
                        setIsPOSModal(true)
                      }}
                    >
                      Add another terminal
                      <Icon id="add-icon-2" width={20} height={20} />
                    </button>
                  </div>

                  <TableComponent
                    rows={(pos?.data.docs ?? []).map((item) => {
                      return {
                        id: item._id,
                        content: [
                          <div
                            key={`${item._id}-creator`}
                            className="flex items-center justify-between"
                          >
                            <p className="text-sec-black">
                              {item.tag}
                              {' - '}
                              <span
                                className={`${
                                  item.status === 'ACTIVE'
                                    ? 'text-success'
                                    : 'text-danger'
                                } text-xs`}
                              >
                                {item.provider}
                              </span>
                            </p>

                            <div className="flex flex-row space-x-4">
                              <button
                                type="button"
                                className="w-fit flex items-center gap-2"
                                onClick={() => {
                                  setSelectedPOSDevice(item)
                                  setIsPOSModal(!isPOSModal)
                                }}
                              >
                                <span>Edit</span>
                                <Icon width={20} height={20} id="edit" />
                              </button>
                              <button
                                type="button"
                                className="w-fit text-danger flex items-center gap-2"
                                onClick={async () => {
                                  setSelectedPOSDevice(item)
                                  setDeviceAction(
                                    item.status === 'ACTIVE'
                                      ? 'Deactivate'
                                      : 'Reactivate',
                                  )
                                  handleModalOpen('confirmCreatorDeleteModal')
                                }}
                              >
                                {item.status === 'ACTIVE' ? (
                                  <span className="text-success">
                                    Deactivate
                                  </span>
                                ) : (
                                  <span className="text-danger">Activate</span>
                                )}
                                {/* <Icon width={20} height={20} id="bin" /> */}
                              </button>
                            </div>
                          </div>,
                        ],
                      }
                    })}
                    loading={fetchingPOS}
                    isEmpty={false}
                    currentPage={getPageFromSkip(query.page!, query.limit!)}
                    totalDataCount={pos?.data.total ?? 0}
                    pageLimit={Number(query.limit!)}
                    onPageChange={(page) => {
                      setPOSQuery({
                        ...posQuery,
                        page: getSkipFromPage(page, query.page!),
                      })
                    }}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium text-base">Creators</p>

                    {!fetchingFulfilmenntCenterDetails && (
                      <button
                        type="button"
                        className="w-fit text-left text-base flex items-center gap-2"
                        onClick={() => {
                          handleModalOpen('selectVendorModal')
                        }}
                      >
                        Add another creator
                        <Icon id="add-icon-2" width={20} height={20} />
                      </button>
                    )}
                  </div>

                  <TableComponent
                    rows={(fulfilmenntCenterDetails?.data?.creators ?? []).map(
                      (item) => {
                        return {
                          id: item._id,
                          content: [
                            <div
                              key={`${item._id}-creator`}
                              className="flex items-center justify-between"
                            >
                              <p className="text-sec-black">{item.name}</p>

                              <button
                                type="button"
                                className="w-fit text-danger flex items-center gap-2"
                                onClick={() => {
                                  setSelectedCreator(item)
                                  handleModalOpen('confirmCreatorDeleteModal')
                                }}
                              >
                                <span>Delete</span>
                                <Icon width={20} height={20} id="bin" />
                              </button>
                            </div>,
                          ],
                        }
                      },
                    )}
                    loading={fetchingFulfilmenntCenterDetails}
                    isEmpty={false}
                    currentPage={getPageFromSkip(query.skip!, query.limit!)}
                    totalDataCount={
                      fulfilmenntCenterDetails?.data?.numberOfCreators ?? 0
                    }
                    pageLimit={Number(query.limit!)}
                    onPageChange={(page) => {
                      setQuery({
                        ...query,
                        skip: getSkipFromPage(page, query.limit!),
                      })
                    }}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <AddLocationModal
        open={modalOpen && modal === 'editFulfilmentCenterAddressModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={async (locationData, resetForm) => {
          const selectedOrigin = origin?.data?.docs?.find(
            (o) => o._id === locationData?.origin?._id,
          )

          const resp = await editFulfilmentCenter({
            id: id ?? '',
            ...values,
            parentFulfilmentCentre:
              values.role === 'PARENT' ? null : values.parentFulfilmentCentre,
            coordinates: [Number(locationData.long), Number(locationData.lat)],
            address: locationData.address,
            origin: selectedOrigin
              ? {
                  _id: selectedOrigin?._id,
                  name: selectedOrigin?.name,
                }
              : undefined,
          })

          if (resp.data?.success) {
            resetForm()
            handleModalClose()
          }
        }}
        title="Edit Fulfilment Center Address"
        fields={['address', 'coordinates']}
        additionalFields={{
          origin: values.origin,
          address: values.address,
          long: String(values.coordinates?.[0] ?? ''),
          lat: String(values.coordinates?.[1] ?? ''),
        }}
        schema={editFulfilmentCenterSchema}
        loading={editingFulfilmentCenter}
        placeholderPrefix="Fulfilment Center"
      >
        {({ formik: modalFormik }) => {
          return (
            <ComboBox
              type="text"
              name="origin"
              placeholder="Select origin"
              value={
                origin?.data?.docs?.find(
                  (origin) => origin._id === modalFormik.values.origin?._id,
                )?.name
              }
              onChange={() => {}}
              onBlur={modalFormik.handleBlur}
              hasIcon={false}
              errors={
                (modalFormik.errors.origin && modalFormik.touched.origin
                  ? modalFormik.errors.origin
                  : '') as string
              }
              menuItems={
                origin?.data?.docs?.map((origin) => {
                  return {
                    name: origin.name,
                    value: {
                      _id: origin._id,
                      name: origin.name,
                    },
                  }
                }) ?? []
              }
              onSelectOption={(selected) => {
                modalFormik.setFieldValue('origin', selected)
              }}
              onSearch={() => {}}
              loading={fetchingOrigin}
              immediate
              position="bottom start"
              menuClassName="!max-h-[250px] shadow-md !z-[999]"
              containerClassName="col-span-full"
              className="col-span-full"
              isDropdown
            />
          )
        }}
      </AddLocationModal>

      <ConfirmationModal
        open={modalOpen && modal === 'confirmCreatorDeleteModal'}
        setOpen={setModalOpen}
        message={`Are you sure you want to ${
          selectedPOSDevice ? `${deviceAction} terminal` : 'delete this creator'
        }?`}
        title={
          selectedPOSDevice ? `${deviceAction} Terminal` : 'Delete Creator'
        }
        onCloseCallback={() => {
          handleModalClose()
          setSelectedPOSDevice(null)
        }}
        onConfirm={async () => {
          if (selectedPOSDevice) {
            const res = await action({
              body: {
                tag: selectedPOSDevice.tag,
                provider: selectedPOSDevice.provider,
                acquisitionDate: selectedPOSDevice.acquisitionDate,
                fulfilmentCentreId: selectedPOSDevice.fulfilmentCentre,
              },
              id: selectedPOSDevice.terminalId,
              action: deviceAction,
            })
            if (res.data?.success) {
              handleModalClose()
            }
          } else {
            const resp = await removeCreators({
              id: id ?? '',
              vendorId: selectedCreator?._id ?? '',
              remove: true,
            })

            if (resp.data?.success) {
              toast.success('Creator removed successfully')
              handleModalClose()
            }
          }
        }}
        loading={removingCreators}
        confirmText={selectedPOSDevice ? `${deviceAction} Terminal` : 'Delete'}
      />

      <SelectCreatorModal
        open={modal === 'selectVendorModal' && modalOpen}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={async (creators) => {
          const toBeAdded = creators
            .map((c) => c._id)
            .filter(
              (c) =>
                !fulfilmenntCenterDetails?.data?.creators
                  ?.map((fc) => fc._id)
                  .includes(c),
            )

          if (toBeAdded.length > 0) {
            const resp = await addCreators({
              id: id ?? '',
              vendors: creators
                .map((c) => c._id)
                .filter(
                  (c) =>
                    !fulfilmenntCenterDetails?.data?.creators
                      ?.map((fc) => fc._id)
                      .includes(c),
                ),
            })

            if (resp.data?.success) {
              toast.success('Creator added to fulfilment centre successfully')
              handleModalClose()
              refetchFulfilmenntCenterDetails()
            }
          } else {
            handleModalClose()
          }
        }}
        selectedCreators={fulfilmenntCenterDetails?.data?.creators?.map((v) => {
          return {
            _id: v._id,
            vendorId: v._id,
            slug: '',
            name: v.name,
            logo: '',
            banner: '',
            extendedBio: '',
          }
        })}
        maxSelection={undefined}
        loading={addingCreators}
        fulfillmentCenters={[]}
      />

      <AddPOSModal
        fulfilmentCentreId={id ?? ''}
        pos={selectedPOSDevice}
        open={isPOSModal}
        setOpen={() => {
          setIsPOSModal(!isPOSModal)
        }}
        onCloseCallback={() => {
          setDeviceAction('')
          setSelectedPOSDevice(null)
        }}
        onSuccessCallback={() => {
          setDeviceAction('')
          setSelectedPOSDevice(null)
        }}
      />
    </Layout>
  )
}
